import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  ArrowLeft,
  Briefcase,
  Plus,
  FileText,
  Receipt,
  Pencil,
  Trash2,
  GripVertical,
  ChevronDown,
  ChevronUp,
  Globe,
  Search,
  BarChart3,
  ShoppingCart,
  Layout,
  Share2,
  Palette,
  Save,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';

// Web & Marketing Project Types with their default fields
const webProjectTypes = [
  {
    id: 'website_design',
    name: 'Website Design',
    icon: Globe,
    color: 'blue',
    description: 'Full website design and development projects',
    fields: [
      { name: 'target_pages', label: 'Target Number of Pages', type: 'number', required: true },
      { name: 'responsive', label: 'Responsive Design Required', type: 'checkbox', required: false },
      { name: 'cms', label: 'CMS Platform', type: 'select', options: ['WordPress', 'Webflow', 'Custom', 'Shopify', 'Other'], required: true },
      { name: 'design_style', label: 'Design Style', type: 'select', options: ['Modern', 'Minimalist', 'Corporate', 'Creative', 'E-commerce'], required: false },
      { name: 'reference_sites', label: 'Reference Websites', type: 'textarea', required: false },
      { name: 'brand_guidelines', label: 'Brand Guidelines Available', type: 'checkbox', required: false },
      { name: 'hosting_needed', label: 'Hosting Required', type: 'checkbox', required: false },
      { name: 'ssl_certificate', label: 'SSL Certificate', type: 'checkbox', required: false }
    ]
  },
  {
    id: 'seo_campaign',
    name: 'SEO Campaign',
    icon: Search,
    color: 'green',
    description: 'Search engine optimization projects',
    fields: [
      { name: 'target_keywords', label: 'Target Keywords', type: 'textarea', required: true },
      { name: 'current_ranking', label: 'Current Search Ranking', type: 'text', required: false },
      { name: 'competitors', label: 'Main Competitors', type: 'textarea', required: false },
      { name: 'local_seo', label: 'Local SEO Focus', type: 'checkbox', required: false },
      { name: 'content_creation', label: 'Content Creation Included', type: 'checkbox', required: false }
    ]
  },
  {
    id: 'ecommerce',
    name: 'E-commerce Store',
    icon: ShoppingCart,
    color: 'purple',
    description: 'Online store setup and configuration',
    fields: [
      { name: 'product_count', label: 'Estimated Product Count', type: 'number', required: true },
      { name: 'platform', label: 'E-commerce Platform', type: 'select', options: ['Shopify', 'WooCommerce', 'Magento', 'BigCommerce', 'Custom'], required: true },
      { name: 'payment_gateways', label: 'Payment Gateways', type: 'text', required: true },
      { name: 'shipping_integration', label: 'Shipping Integration Required', type: 'checkbox', required: false },
      { name: 'inventory_management', label: 'Inventory Management', type: 'checkbox', required: false },
      { name: 'multi_currency', label: 'Multi-Currency Support', type: 'checkbox', required: false }
    ]
  },
  {
    id: 'landing_page',
    name: 'Landing Page',
    icon: Layout,
    color: 'orange',
    description: 'Single page marketing campaigns',
    fields: [
      { name: 'campaign_goal', label: 'Campaign Goal', type: 'select', options: ['Lead Generation', 'Product Launch', 'Event Registration', 'App Download', 'Other'], required: true },
      { name: 'a_b_testing', label: 'A/B Testing Required', type: 'checkbox', required: false },
      { name: 'form_fields', label: 'Form Fields Needed', type: 'textarea', required: false },
      { name: 'analytics_tracking', label: 'Analytics Tracking', type: 'checkbox', required: false }
    ]
  },
  {
    id: 'social_media',
    name: 'Social Media Management',
    icon: Share2,
    color: 'pink',
    description: 'Social media marketing and management',
    fields: [
      { name: 'platforms', label: 'Target Platforms', type: 'text', required: true },
      { name: 'posts_per_week', label: 'Posts Per Week', type: 'number', required: true },
      { name: 'content_calendar', label: 'Content Calendar Included', type: 'checkbox', required: false },
      { name: 'paid_ads', label: 'Paid Advertising', type: 'checkbox', required: false }
    ]
  },
  {
    id: 'branding',
    name: 'Branding & Identity',
    icon: Palette,
    color: 'indigo',
    description: 'Brand identity and visual design',
    fields: [
      { name: 'logo_design', label: 'Logo Design', type: 'checkbox', required: false },
      { name: 'color_palette', label: 'Color Palette', type: 'checkbox', required: false },
      { name: 'typography', label: 'Typography Selection', type: 'checkbox', required: false },
      { name: 'brand_guidelines', label: 'Brand Guidelines Document', type: 'checkbox', required: false }
    ]
  }
];

// Quote Templates for Web & Marketing
const webQuoteTemplates = [
  {
    id: 'standard_web',
    name: 'Standard Web Quote',
    description: 'General website development quote',
    sections: ['Design', 'Development', 'Content', 'Testing', 'Deployment'],
    defaultItems: [
      { description: 'UI/UX Design', quantity: 1, unit: 'project', rate: 2500 },
      { description: 'Frontend Development', quantity: 1, unit: 'project', rate: 3500 },
      { description: 'Backend Development', quantity: 1, unit: 'project', rate: 3000 },
      { description: 'Content Migration', quantity: 1, unit: 'project', rate: 500 },
      { description: 'QA Testing', quantity: 1, unit: 'project', rate: 750 }
    ]
  },
  {
    id: 'seo_service',
    name: 'SEO Service Quote',
    description: 'Monthly SEO retainer quote',
    sections: ['Audit', 'On-Page', 'Off-Page', 'Reporting'],
    defaultItems: [
      { description: 'Initial SEO Audit', quantity: 1, unit: 'project', rate: 1500 },
      { description: 'Keyword Research', quantity: 1, unit: 'project', rate: 800 },
      { description: 'On-Page Optimization', quantity: 1, unit: 'month', rate: 1200 },
      { description: 'Link Building', quantity: 1, unit: 'month', rate: 1500 },
      { description: 'Monthly Reporting', quantity: 1, unit: 'month', rate: 300 }
    ]
  },
  {
    id: 'retainer',
    name: 'Monthly Retainer Quote',
    description: 'Ongoing monthly services',
    sections: ['Maintenance', 'Updates', 'Support'],
    defaultItems: [
      { description: 'Website Maintenance', quantity: 1, unit: 'month', rate: 500 },
      { description: 'Security Updates', quantity: 1, unit: 'month', rate: 200 },
      { description: 'Content Updates (up to 5/month)', quantity: 1, unit: 'month', rate: 400 },
      { description: 'Priority Support', quantity: 1, unit: 'month', rate: 300 }
    ]
  }
];

const FormTemplatesWeb = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('projects');
  const [selectedProjectType, setSelectedProjectType] = useState(null);
  const [selectedQuoteTemplate, setSelectedQuoteTemplate] = useState(null);
  const [isEditFieldOpen, setIsEditFieldOpen] = useState(false);
  const [expandedProject, setExpandedProject] = useState(null);

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-50 text-blue-600 border-blue-200',
      green: 'bg-green-50 text-green-600 border-green-200',
      purple: 'bg-purple-50 text-purple-600 border-purple-200',
      orange: 'bg-orange-50 text-orange-600 border-orange-200',
      pink: 'bg-pink-50 text-pink-600 border-pink-200',
      indigo: 'bg-indigo-50 text-indigo-600 border-indigo-200'
    };
    return colors[color] || colors.blue;
  };

  const getFieldTypeLabel = (type) => {
    const types = {
      text: 'Text Input',
      number: 'Number',
      textarea: 'Text Area',
      select: 'Dropdown',
      checkbox: 'Checkbox',
      date: 'Date Picker'
    };
    return types[type] || type;
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/settings')}
            className="shrink-0"
          >
            <ArrowLeft size={20} />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center">
              <Briefcase size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Web & Marketing Templates</h1>
              <p className="text-slate-500">Manage project types and quote templates for digital agencies</p>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1">
            <TabsTrigger value="projects" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
              <FileText size={16} className="mr-2" />
              Project Types
            </TabsTrigger>
            <TabsTrigger value="quotes" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
              <Receipt size={16} className="mr-2" />
              Quote Templates
            </TabsTrigger>
          </TabsList>

          {/* Project Types Tab */}
          <TabsContent value="projects" className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600">
                Configure the fields shown when creating projects of each type
              </p>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus size={16} className="mr-2" />
                Add Project Type
              </Button>
            </div>

            <div className="grid gap-4">
              {webProjectTypes.map((projectType) => {
                const Icon = projectType.icon;
                const isExpanded = expandedProject === projectType.id;
                
                return (
                  <Card key={projectType.id} className="border-slate-200 shadow-sm">
                    <CardHeader 
                      className="cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => setExpandedProject(isExpanded ? null : projectType.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getColorClasses(projectType.color)}`}>
                            <Icon size={20} />
                          </div>
                          <div>
                            <CardTitle className="text-lg font-semibold text-slate-900">
                              {projectType.name}
                            </CardTitle>
                            <CardDescription>{projectType.description}</CardDescription>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-slate-500">
                            {projectType.fields.length} fields
                          </span>
                          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>
                      </div>
                    </CardHeader>
                    
                    {isExpanded && (
                      <CardContent className="border-t border-slate-100 pt-4">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="text-sm font-semibold text-slate-700">Form Fields</h4>
                            <Button size="sm" variant="outline">
                              <Plus size={14} className="mr-1" />
                              Add Field
                            </Button>
                          </div>
                          
                          {projectType.fields.map((field, idx) => (
                            <div 
                              key={idx}
                              className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100"
                            >
                              <div className="flex items-center gap-3">
                                <GripVertical size={16} className="text-slate-400 cursor-grab" />
                                <div>
                                  <p className="font-medium text-slate-900">{field.label}</p>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">
                                      {getFieldTypeLabel(field.type)}
                                    </span>
                                    {field.required && (
                                      <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">
                                        Required
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button variant="ghost" size="sm">
                                  <Pencil size={14} />
                                </Button>
                                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700">
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Quote Templates Tab */}
          <TabsContent value="quotes" className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600">
                Configure quote templates that can be sent to clients
              </p>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus size={16} className="mr-2" />
                Add Quote Template
              </Button>
            </div>

            <div className="grid gap-4">
              {webQuoteTemplates.map((template) => (
                <Card key={template.id} className="border-slate-200 shadow-sm">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                          <Receipt size={20} className="text-green-600" />
                        </div>
                        <div>
                          <CardTitle className="text-lg font-semibold text-slate-900">
                            {template.name}
                          </CardTitle>
                          <CardDescription>{template.description}</CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm">
                          <Eye size={14} className="mr-1" />
                          Preview
                        </Button>
                        <Button variant="outline" size="sm">
                          <Pencil size={14} className="mr-1" />
                          Edit
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="border-t border-slate-100">
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-sm font-semibold text-slate-700 mb-2">Sections</h4>
                        <div className="flex flex-wrap gap-2">
                          {template.sections.map((section, idx) => (
                            <span key={idx} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                              {section}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-slate-700 mb-2">Default Line Items</h4>
                        <div className="bg-slate-50 rounded-lg p-3">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-slate-500">
                                <th className="pb-2">Description</th>
                                <th className="pb-2 text-right">Qty</th>
                                <th className="pb-2 text-right">Unit</th>
                                <th className="pb-2 text-right">Rate</th>
                              </tr>
                            </thead>
                            <tbody>
                              {template.defaultItems.map((item, idx) => (
                                <tr key={idx} className="border-t border-slate-200">
                                  <td className="py-2 text-slate-900">{item.description}</td>
                                  <td className="py-2 text-right text-slate-600">{item.quantity}</td>
                                  <td className="py-2 text-right text-slate-600">{item.unit}</td>
                                  <td className="py-2 text-right text-slate-900">${item.rate.toLocaleString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default FormTemplatesWeb;
